import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polygon, Popup, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useDistricts, useIncident } from "@/hooks/useData";
import { usePrefs } from "@/context/PrefsContext";

export const pinIcon = L.divIcon({ className: "", html: '<div class="cad-pin"></div>', iconSize: [18, 18], iconAnchor: [9, 18] });
const DISTRICT_COLORS = { blue: "#2563EB", teal: "#0D9488", amber: "#f59e0b", red: "#ef4444", green: "#10b981", slate: "#64748b" };

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, Math.max(map.getZoom(), 13)); }, [center?.[0], center?.[1]]); // eslint-disable-line
  return null;
}

function ContextMenu({ onDrop }) {
  const [pos, setPos] = useState(null);
  useMapEvents({ contextmenu(e) { setPos(e.latlng); }, click() { setPos(null); } });
  if (!pos) return null;
  return (
    <Popup position={pos} eventHandlers={{ remove: () => setPos(null) }} closeButton={false}>
      <div className="text-xs space-y-1" data-testid="map-context-menu">
        <div className="mono text-[10px] text-slate-500">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</div>
        <button data-testid="map-drop-pin-button" onClick={() => { onDrop(pos); setPos(null); }} className="px-2 py-1 rounded bg-[#2563EB] text-white font-semibold">Drop a Pin here</button>
      </div>
    </Popup>
  );
}

export function DistrictLayers({ districts }) {
  return districts.map((d) => (
    <Polygon key={d.id} positions={d.polygon} pathOptions={{ color: DISTRICT_COLORS[d.color] || DISTRICT_COLORS.blue, weight: 2, fillOpacity: 0.08 }}>
      <Tooltip sticky>{d.name}</Tooltip>
    </Polygon>
  ));
}

export function MapCard({ incidentId }) {
  const incQ = useIncident(incidentId);
  const distQ = useDistricts();
  const { settings } = usePrefs();
  const qc = useQueryClient();
  const inc = incQ.data;
  const hasCoords = inc?.location_lat != null;
  const center = hasCoords ? [inc.location_lat, inc.location_lng] : (settings.map_center || [39.7990, -89.6500]);

  const drop = async (latlng) => {
    if (!inc) { toast.error("Select an incident first"); return; }
    if (inc.status === "finished") { toast.error("Finished incidents are read-only"); return; }
    try {
      const { data } = await api.post(`/incidents/${inc.id}/location-point`, { lat: latlng.lat, lng: latlng.lng, source: "pin" });
      qc.setQueryData(["incident", inc.id], data);
      qc.invalidateQueries({ queryKey: ["board"] });
      toast.success(`Pin dropped — incident location set${data.district_name ? ` · ${data.district_name}` : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-2" data-testid="map-card">
      <div className="flex items-center gap-2">
        <div>
          <div className="overline-label">Map · districts & pin-drop</div>
          <div className="display text-base font-semibold">{inc ? `${inc.incident_number || "Draft"} · ${inc.location_raw || "Location pending"}` : "No incident selected"}</div>
        </div>
        <span className="ml-auto text-[11px] text-muted-foreground">Right-click the map to drop a pin — the pin becomes the incident location.</span>
      </div>
      {inc && (
        <div className="text-xs flex flex-wrap gap-2" data-testid="map-status-line">
          {hasCoords ? <span className="chip mono border-blue-500/50 text-blue-700 dark:text-blue-300">{inc.location_source?.toUpperCase()} · {inc.location_lat.toFixed(5)}, {inc.location_lng.toFixed(5)}</span>
            : <span className="chip border-amber-500/50 text-amber-700 dark:text-amber-300" data-testid="map-no-coords">No coordinates resolved — drop a pin</span>}
          {inc.district_name && <span className="chip mono border-primary/50 text-primary">{inc.district_name}</span>}
          {inc.recommendation?.call_sign && <span className="chip border-teal-500/50 text-teal-700 dark:text-teal-300">Recommended {inc.recommendation.call_sign}</span>}
        </div>
      )}
      <div className="h-[420px] rounded-md overflow-hidden border">
        <MapContainer center={center} zoom={settings.map_zoom || 12} scrollWheelZoom style={{ height: "100%" }}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <DistrictLayers districts={distQ.data || []} />
          {hasCoords && <Marker position={[inc.location_lat, inc.location_lng]} icon={pinIcon}><Popup>{inc.incident_number || "Draft"} · {inc.location_source}</Popup></Marker>}
          <Recenter center={hasCoords ? [inc.location_lat, inc.location_lng] : null} />
          <ContextMenu onDrop={drop} />
        </MapContainer>
      </div>
    </div>
  );
}
